import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Eye, EyeOff, AlertCircle, Loader2, Calendar } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import dayjs from 'dayjs';

function SocialAuthButtons({ context = 'signup' }) {
  const label = (p) => (context === 'signup' ? `Sign up with ${p}` : `Continue with ${p}`);
  const stub = (provider) => toast.info(`${provider} sign-in coming soon — use Email for now`);

  return (
    <div className="flex flex-col gap-2.5 mb-2">
      <button data-testid="social-google-btn" onClick={() => stub('Google')} type="button"
        className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl border-[1.5px] border-gray-200 bg-white text-gray-900 text-[15px] font-bold hover:bg-gray-50 transition-colors">
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
        {label('Google')}
      </button>
      <button data-testid="social-apple-btn" onClick={() => stub('Apple')} type="button"
        className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl border-[1.5px] border-gray-900 bg-gray-900 text-white text-[15px] font-bold hover:bg-gray-800 transition-colors">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
        {label('Apple')}
      </button>
    </div>
  );
}

function AuthDivider() {
  return (
    <div className="flex items-center gap-3 my-3">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-xs font-semibold text-gray-400 lowercase tracking-wide">or sign up with email</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim() || !email.trim() || !password) { setErr('All fields are required'); return; }
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    if (!dob) { setErr('Please select your date of birth'); return; }
    const age = dayjs().diff(dayjs(dob), 'year');
    if (age < 13) { setErr('You must be at least 13 years old to register'); return; }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim(), dob);
      toast.success('Account created!');
      navigate('/');
    } catch (e) {
      setErr(e.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center pt-8 sm:pt-16 px-4">
      <div className="w-full max-w-md" data-testid="register-page">
        <button data-testid="register-back-btn" onClick={() => navigate(-1)}
          className="mb-6 p-1 -ml-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>

        <div className="flex justify-center mb-6">
          <img src="/radius-icon.png" alt="RADIUS" className="h-16 w-16 rounded-2xl object-cover" data-testid="register-brand-logo" />
        </div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Create your account</h1>
        <p className="mt-2 text-gray-500 mb-5">Takes less than a minute.</p>

        <SocialAuthButtons context="signup" />
        <AuthDivider />

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="register-name" className="text-xs font-bold text-gray-500">Full name</label>
            <input id="register-name" data-testid="register-name-input" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Jane Doe"
              className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="register-email" className="text-xs font-bold text-gray-500">Email</label>
            <input id="register-email" data-testid="register-email-input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="register-password" className="text-xs font-bold text-gray-500">Password (min 8 chars)</label>
            <div className="relative">
              <input id="register-password" data-testid="register-password-input"
                type={showPw ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Choose a strong password"
                className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 pr-12 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="register-dob" className="text-xs font-bold text-gray-500">
              Date of birth <span className="text-brand-magenta">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                id="register-dob"
                data-testid="register-dob-input"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={dayjs().subtract(13, 'year').format('YYYY-MM-DD')}
                min={dayjs().subtract(100, 'year').format('YYYY-MM-DD')}
                className="w-full bg-white border-2 border-gray-200 rounded-xl pl-10 pr-4 py-3.5 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {err && (
            <div data-testid="register-error" className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-semibold">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {err}
            </div>
          )}

          <button data-testid="register-submit-btn" type="submit" disabled={loading}
            className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-70 text-white py-4 rounded-2xl font-extrabold text-base transition-colors mt-1">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Create Account'}
          </button>

          <button type="button" data-testid="register-goto-login-btn" onClick={() => navigate('/login')}
            className="text-center py-2 text-sm">
            <span className="text-gray-500">Already have an account? </span>
            <span className="text-brand-magenta font-bold">Sign in</span>
          </button>
        </form>
      </div>
    </div>
  );
}
